// The course's final exam: five real past market days in the practice account (PracticePage.js
// with ?exam=1…5), graded on process, not profit. Each day's trades are checked against the
// exam's rules; the graduation report (CoursePage.js) adds the five days up.
import { contractOf } from '../practiceSim';

export const EXAM_DAYS = 5;
export const EXAM_SYMBOL = 'MMX'; // moves and pays like MES
export const EXAM_ACCOUNT = { balance: 50000, maxLoss: 2000, trailing: 'intraday', dailyLoss: 500 };
export const EXAM_RULES = { maxRisk: 200, minRR: 1.5, maxLosses: 2, coolDown: 300, maxTrades: 6 };
export const PASS_AVERAGE = 0.8;

// The rules each day is checked on, in order. Text for the exam page and the report.
export const EXAM_CHECKS = [
  { id: 'stops', text: { en: 'A stop on every trade', es: 'Un stop en cada operación' } },
  { id: 'targets', text: { en: 'A target of at least 1.5R on every trade', es: 'Un objetivo de al menos 1.5R en cada operación' } },
  { id: 'risk', text: { en: 'No more than $200 at risk per trade', es: 'No más de $200 en riesgo por operación' } },
  { id: 'dailyStop', text: { en: 'Stop after 2 losing trades (and never hit the $500 daily limit)', es: 'Parar después de 2 operaciones perdedoras (y nunca llegar al límite diario de $500)' } },
  { id: 'revenge', text: { en: 'No trade within 5 minutes of a loss', es: 'Ninguna operación en los 5 minutos después de una pérdida' } },
  { id: 'sizeUp', text: { en: 'No bigger size right after a loss', es: 'No subir el tamaño justo después de una pérdida' } },
  { id: 'overtrading', text: { en: 'No more than 6 trades', es: 'No más de 6 operaciones' } },
];

// The exam's days: five fixed practice days (practiceDays.js: a trend day, a failed breakout, a
// range day, a news spike and a V reversal), the same for everyone, so the grades are fair.
export const examDay = (n) => `exam-${n}`;

const riskOf = (t, point) => (Number.isFinite(t.stopPrice) ? Math.abs(t.entryPrice - t.stopPrice) * point * t.size : null);

// One exam day's grade: each rule passed or not (with how many trades broke it), the day's net
// P&L and R, and the score (rules passed out of all of them).
export function gradeExamDay(trades, { symbol = EXAM_SYMBOL, blown = null } = {}) {
  const point = contractOf(symbol).point;
  const list = trades.slice().sort((a, b) => a.entryTime - b.entryTime);
  const broke = Object.fromEntries(EXAM_CHECKS.map((c) => [c.id, 0]));
  let losses = 0;
  let lastLoss = null;
  list.forEach((t, i) => {
    const risk = riskOf(t, point);
    if (risk === null) broke.stops += 1;
    const reward = Number.isFinite(t.targetPrice) ? Math.abs(t.targetPrice - t.entryPrice) * point * t.size : null;
    if (risk === null || reward === null || reward / risk < EXAM_RULES.minRR) broke.targets += 1;
    if (risk === null || risk > EXAM_RULES.maxRisk + 0.01) broke.risk += 1;
    if (losses >= EXAM_RULES.maxLosses) broke.dailyStop += 1;
    if (lastLoss && t.entryTime - lastLoss.exitTime < EXAM_RULES.coolDown) broke.revenge += 1;
    if (lastLoss && i > 0 && list[i - 1] === lastLoss && t.size > lastLoss.size) broke.sizeUp += 1;
    if (t.net < 0) { losses += 1; lastLoss = t; } else lastLoss = null;
  });
  if (blown && blown.rule === 'dailyLoss') broke.dailyStop += 1;
  if (list.length > EXAM_RULES.maxTrades) broke.overtrading = list.length - EXAM_RULES.maxTrades;
  const checks = EXAM_CHECKS.map((c) => ({ id: c.id, ok: broke[c.id] === 0, times: broke[c.id] }));
  const rs = list.map((t) => { const risk = riskOf(t, point); return risk ? t.net / risk : null; }).filter((r) => r !== null);
  return {
    checks,
    score: checks.filter((c) => c.ok).length,
    total: checks.length,
    trades: list.length,
    net: Math.round(list.reduce((s, t) => s + t.net, 0) * 100) / 100,
    r: Math.round(rs.reduce((s, r) => s + r, 0) * 100) / 100,
  };
}

// The graduation report from the saved exam days ({ lessonId: 'final-exam-N', details }).
export function examReport(progress) {
  const days = [];
  for (let n = 1; n <= EXAM_DAYS; n += 1) {
    const row = progress.find((p) => p.lessonId === `final-exam-${n}` && p.done && p.details);
    if (row) days.push({ n, ...row.details });
  }
  const done = days.length === EXAM_DAYS;
  const average = days.length ? days.reduce((s, d) => s + d.score / d.total, 0) / days.length : 0;
  const broken = EXAM_CHECKS.map((c) => ({ id: c.id, text: c.text, times: days.reduce((s, d) => s + ((d.checks.find((x) => x.id === c.id) || {}).times || 0), 0) }))
    .filter((c) => c.times > 0).sort((a, b) => b.times - a.times);
  return {
    days, done, average,
    trades: days.reduce((s, d) => s + d.trades, 0),
    net: days.reduce((s, d) => s + d.net, 0),
    r: days.reduce((s, d) => s + d.r, 0),
    broken,
    graduated: done && average >= PASS_AVERAGE && days.reduce((s, d) => s + d.trades, 0) >= 3,
  };
}
